import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, downloadFile, blobErrorDetail, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShoppingCart, PackageCheck, Download, ArrowUpRight } from "lucide-react";
import { fmtDate } from "@/lib/workflow";

function ProcessDialog({ req, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ erp_reference: "", notes: "" });
  const submit = async () => {
    try {
      await api.patch(`/order-requests/${req.request_id}/process`, form);
      toast.success(`Order ${req.project_number} marked as processed`);
      setOpen(false);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <>
      <Button size="sm" className="bg-[#2563EB] hover:bg-[#1d4ed8] h-8" onClick={() => setOpen(true)} data-testid={`order-process-btn-${req.request_id}`}><PackageCheck className="h-3.5 w-3.5 mr-1" />Mark processed</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Mark order as processed — {req.project_number}</DialogTitle><DialogDescription>Confirms the ERP order was created manually in your ERP system. No automatic ERP integration is performed.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">ERP order reference (optional)</Label><Input value={form.erp_reference} onChange={(e) => setForm({ ...form, erp_reference: e.target.value })} placeholder="e.g. PAN-2026-00123" data-testid="order-process-ref-input" /></div>
            <div><Label className="text-xs">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="order-process-notes-input" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="order-process-confirm-btn">Confirm processed</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function OrderRequestsPage() {
  const [data, setData] = useState({ items: [], can_process: false, counts: { requested: 0, processed: 0 } });
  const [status, setStatus] = useState("all");
  const load = () => api.get("/order-requests", { params: { status } }).then((r) => setData(r.data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-8" data-testid="order-requests-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="overline">ERP handover</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Order Requests</h1>
          <p className="text-sm text-slate-500 mt-1">Projects that passed the ERP Readiness Gate and requested order creation. {data.can_process ? "You can mark requests as processed." : "Only Admin / ERP Operator can mark requests as processed."}</p>
        </div>
        <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1">
          {["all", "requested", "processed"].map((s) => (
            <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-md text-sm capitalize ${status === s ? "bg-[#2563EB] text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`orders-filter-${s}`}>
              {s}{s !== "all" && <span className="ml-1 font-mono text-xs opacity-70">{data.counts[s]}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="order-requests-table">
        <Table>
          <TableHeader className="bg-slate-50"><TableRow>
            <TableHead>Project</TableHead><TableHead>Customer / Brand</TableHead><TableHead>Economic File</TableHead><TableHead>Requested</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.items.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-500"><ShoppingCart className="h-6 w-6 mx-auto text-slate-400 mb-2" />No order requests{status !== "all" ? ` with status ${status}` : ""}.</TableCell></TableRow>
            ) : data.items.map((r) => (
              <TableRow key={r.request_id} data-testid={`order-row-${r.request_id}`}>
                <TableCell>
                  <Link to={`/projects/${r.project_id}`} className="font-mono text-xs bg-slate-900 text-white rounded px-1.5 py-0.5">{r.project_number}</Link>
                  <div className="text-xs text-slate-500 mt-1 truncate max-w-[220px]">{r.project_title}</div>
                </TableCell>
                <TableCell><div className="text-sm text-slate-900">{r.customer_name}</div><div className="text-xs text-slate-500">{r.brand} · PM {r.project_manager_name}</div></TableCell>
                <TableCell>
                  <button type="button" onClick={async () => { try { await downloadFile(`/documents/${r.document_id}/download`, r.economic_file_name); } catch (e) { toast.error(await blobErrorDetail(e)); } }} className="inline-flex items-center text-xs text-blue-700 hover:underline" data-testid={`order-ef-download-${r.request_id}`}>
                    <Download className="h-3 w-3 mr-1" />{r.economic_file_name} <span className="ml-1 text-slate-400">rev {r.economic_file_revision}</span>
                  </button>
                </TableCell>
                <TableCell className="text-xs text-slate-600"><div>{r.requested_by_name}</div><div className="font-mono text-slate-400">{fmtDate(r.requested_at)}</div>{r.notes && <div className="italic mt-1 max-w-[200px] truncate">{r.notes}</div>}</TableCell>
                <TableCell>
                  {r.status === "requested" ? <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Requested</Badge> : (
                    <div><Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Processed</Badge>
                      <div className="text-[11px] text-slate-500 mt-1">{r.processed_by_name} · {fmtDate(r.processed_at)}</div>
                      {r.erp_reference && <div className="text-[11px] font-mono text-slate-700">ERP {r.erp_reference}</div>}</div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {r.status === "requested" && data.can_process ? <ProcessDialog req={r} onDone={load} /> : <Link to={`/projects/${r.project_id}`} className="text-slate-400 hover:text-slate-900"><ArrowUpRight className="h-4 w-4 inline" /></Link>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
